import React from 'react';
import PropTypes from 'prop-types';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import fr from 'facet-rest';

const CollectionContext = React.createContext(undefined);

// Actions
const INITIALIZE_STARTED = 'initialize-started';
const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
const INITIALIZE_FAILED = 'initialize-failed';

const SET_COLLECTION = 'set-collection';

const REFRESH_STARTED = 'collection-started';
const REFRESH_SUCCEEDED = 'collection-succeeded';
const REFRESH_FAILED = 'collection-refresh-failed';

const init = (collection = null) => {
  return {
    isInitializing: true,
    initializingError: null,
    collection,
    isRefreshing: false,
    refreshError: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case SET_COLLECTION: {
      return {
        ...state,
        collection: action.collection,
      };
    }
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        collection: action.collection,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case REFRESH_STARTED: {
      return {
        ...state,
        isRefreshing: true,
        refreshError: null,
      };
    }
    case REFRESH_SUCCEEDED: {
      return {
        ...state,
        isRefreshing: false,
        refreshError: null,
        collection: action.collection,
      };
    }
    case REFRESH_FAILED: {
      return {
        ...state,
        isRefreshing: false,
        refreshError: action.refreshError,
      };
    }
    default: {
      throw new Error(`Unhandled CollectionContext action type: ${action.type}`);
    }
  }
};

const CollectionProvider = ({
  collection,
  children,
  link,
}) => {
  const parentContext = React.useContext(CollectionContext);

  const [state, dispatch] = React.useReducer(reducer, collection, init);

  const refresh = React.useCallback(async () => {
    const { collection: coll } = state;

    if (coll === null) {
      return;
    }

    if (coll.resources().length() === 0) {
      return;
    }

    try {
      dispatch({ type: REFRESH_STARTED });

      const next = await coll.refresh();

      dispatch({ type: REFRESH_SUCCEEDED, collection: next });
    } catch (err) {
      dispatch({ type: REFRESH_FAILED, refreshError: err });
    }
  }, [state]);

  const rootRefresh = React.useMemo(async () => {
    let rootCtxRefresh = refresh;
    let currentCtx = parentContext;
    while (currentCtx) {
      if (currentCtx.parentContext) {
        rootCtxRefresh = currentCtx.refresh;
      } else {
        currentCtx = currentCtx.parentContext;
      }
    }

    return rootCtxRefresh;
  }, [refresh, state?.collection, parentContext?.collection]);

  const setCollection = React.useCallback(async (nextCollection) => {
    try {
      dispatch({ type: SET_COLLECTION, collection: nextCollection });
    } catch (err) {
      dispatch({ type: REFRESH_FAILED, refreshError: err });
    }
  }, [dispatch]);

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      collection: state.collection,
      isRefreshing: state.isRefreshing,
      refreshError: state.refreshError,
      refresh,
      setCollection,
      parentContext,
    };
  }, [
    state,
    parentContext,
    refresh,
    rootRefresh,
  ]);

  const initialize = async () => {
    try {
      dispatch({ type: INITIALIZE_STARTED });

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        collection: await fr.http.get(link),
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_FAILED, initializationError: err });
    }
  };

  useComponentDidMount(() => initialize());

  return (
    <CollectionContext.Provider value={memoizedValue}>
      {children}
    </CollectionContext.Provider>
  );
};

const useCollection = () => {
  const context = React.useContext(CollectionContext);
  if (context === undefined) {
    throw new Error('useCollection must be used within a CollectionProvider');
  }
  return context;
};

CollectionProvider.defaultProps = {
  link: null,
  collection: null,
};

CollectionProvider.propTypes = {
  link: PropTypes.string,
  collection: PropTypes.object,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  CollectionProvider,
  useCollection,
};
